import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { scrollToId, waLink } from "@/lib/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1675657144285-7daf131132de?auto=format&fit=crop&w=1920&q=85";

export const Hero = ({ config }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Private Bali villa at dusk"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-ink/20" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex min-h-screen flex-col justify-end pb-24 pt-32 sm:pb-28"
      >
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="mb-6 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/70"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-gold" />
            A Private Collection — Bali, Indonesia
          </motion.p>

          <h1 className="font-serif text-[13vw] font-light leading-[0.98] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.5}>Private Bali Villas,</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="italic text-gold">Direct Concierge</span> Booking.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/80 sm:text-lg"
            data-testid="hero-subheadline"
          >
            A handpicked portfolio of six architectural sanctuaries designed for
            unhurried luxury. Book directly with our team for exclusive perks
            and seamless arrivals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              data-testid="hero-whatsapp-cta"
              href={waLink(config.whatsapp_number, config.whatsapp_default_message)}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-500 hover:bg-ivory"
            >
              <MessageCircle size={16} className="transition-transform duration-500 group-hover:-rotate-12" />
              Inquire via WhatsApp
            </a>
            <button
              data-testid="hero-explore-cta"
              onClick={() => scrollToId("villas")}
              className="group flex items-center justify-center gap-3 border border-ivory/40 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-500 hover:border-gold hover:text-gold"
            >
              Explore 6 Sanctuaries
              <ArrowDown size={14} className="transition-transform duration-500 group-hover:translate-y-1" />
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-ivory/50 sm:flex"
      >
        Scroll
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ArrowDown size={12} />
        </motion.span>
      </motion.div>
    </section>
  );
};
